import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DemoApp from './DemoApp';
import RegistroDemoModal from '../components/RegistroDemoModal';

const IMAGENES = Array.from({ length: 25 }, (_, i) => {
  const num = String(i + 1).padStart(2, '0');
  return `/login-bg/bg-${num}.jpg`;
});

const INTERVALO_MS = 6000;
const FADE_MS = 2000;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCodigo, setErrorCodigo] = useState(null);
  const [cargando, setCargando] = useState(false);

  const [indice, setIndice] = useState(0);
  const [mostrarRegistro, setMostrarRegistro] = useState(false);
  
  // ✅ La presentación carga por defecto
  const [vistaIzquierda, setVistaIzquierda] = useState('gamma'); 

  const orden = useMemo(() => shuffle(IMAGENES), []);

  useEffect(() => {
    orden.forEach((src) => {
      const img = new Image();
      img.onerror = () => console.warn('[Login] No se pudo cargar:', src);
      img.src = src;
    });
  }, [orden]);

  useEffect(() => {
    if (orden.length === 0) return;
    const t = setInterval(() => {
      setIndice((prev) => (prev + 1) % orden.length);
    }, INTERVALO_MS);
    return () => clearInterval(t);
  }, [orden.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCargando(true);
    setError('');
    setErrorCodigo(null);

    try {
      const usuarioLogueado = await login(email, password);
      const rol = usuarioLogueado?.rol;
      if (rol === 'operador' || rol === 'auxiliar') {
        navigate('/alimentacion/actividades', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      const data = err.response?.data || {};
      setError(data.error || 'Error al iniciar sesión');
      setErrorCodigo(data.codigo || null);
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gray-900">
      {/* Fondo con imágenes rotando */}
      <div className="absolute inset-0">
        {orden.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${src})`,
              opacity: i === indice ? 1 : 0,
              transition: `opacity ${FADE_MS}ms ease-in-out`,
            }}
            aria-hidden="true"
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/40 to-black/60" />
      </div>

      {/* Indicadores */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {orden.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndice(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === indice ? 'w-8 bg-white' : 'w-3 bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`Ir a imagen ${i + 1}`}
          />
        ))}
      </div>

      {/* Contenido principal: 2 columnas */}
      <div className="relative z-10 min-h-screen flex items-center justify-center gap-6 xl:gap-12 px-4 py-8 lg:px-12">

        {/* ─── IZQUIERDA: Toggle entre Gamma y Demo App ─── */}
        <div className="hidden lg:flex flex-col items-center flex-shrink-0 gap-4 w-full max-w-[850px]">
          
          {/* Botones Toggle (Tabs) */}
          <div className="flex bg-black/40 backdrop-blur-md rounded-full p-1 border border-white/20 shadow-xl">
            <button
              type="button"
              onClick={() => setVistaIzquierda('gamma')}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                vistaIzquierda === 'gamma'
                  ? 'bg-white text-blue-900 shadow-md transform scale-105'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              📊 Presentación
            </button>
            <button
              type="button"
              onClick={() => setVistaIzquierda('demo')}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                vistaIzquierda === 'demo'
                  ? 'bg-white text-blue-900 shadow-md transform scale-105'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              📱 App Móvil
            </button>
          </div>

          {/* ✅ Contenedor con proporción 16:9 dinámico */}
          <div className="w-full flex items-center justify-center">
            {vistaIzquierda === 'demo' ? (
              <div className="animate-fade-in transition-all">
                <DemoApp embebido />
              </div>
            ) : (
              <div className="w-full" style={{ aspectRatio: '16 / 9' }}>
                <iframe
                  src="https://gamma.app/embed/bnoviydgjms0mh1"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)'
                  }}
                  allow="fullscreen"
                  title="Digitalice su finca camaronera"
                />
              </div>
            )}
          </div>
        </div>

        {/* ─── DERECHA: Formulario de login ─── */}
        <div
          className="w-full max-w-md rounded-2xl p-8 shadow-2xl border border-white/20 flex-shrink-0"
          style={{
            backgroundColor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          <div className="text-center mb-6">
            <img
              src="/logo.png"
              alt="Logo"
              className="mx-auto mb-2"
              style={{ width: 110, height: 110, objectFit: 'contain' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fb = e.currentTarget.nextElementSibling;
                if (fb) fb.classList.remove('hidden');
              }}
            />
            <div className="text-6xl mb-2 hidden">🦐</div>
            <h1 className="text-2xl font-bold text-primary">Gestión de Producción</h1>
            <h1 className="text-2xl font-bold text-primary">& Alimentación Automática</h1>
            <p className="text-sm text-gray-500 mt-1">Panel de administración</p>
          </div>

          <form onSubmit={handleSubmit}>
            <label className="block mb-4">
              <span className="text-sm font-medium">Usuario</span>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-white/90"
                placeholder="Usuario"
                required
                autoComplete="username"
                autoFocus
              />
            </label>

            <label className="block mb-4">
              <span className="text-sm font-medium">Contraseña</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-white/90"
                required
                autoComplete="current-password"
              />
            </label>

            {error && errorCodigo === 'FINCA_INACTIVA' && (
              <div className="mb-4 p-4 rounded-lg border-2 border-amber-400 bg-amber-50">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">🏢</span>
                  <div className="flex-1">
                    <div className="font-bold text-amber-900 mb-1">Finca inactiva</div>
                    <p className="text-sm text-amber-800 leading-snug">{error}</p>
                    <p className="text-xs text-amber-700 mt-2 pt-2 border-t border-amber-200">
                      Si crees que es un error, contacta al administrador del sistema.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && errorCodigo !== 'FINCA_INACTIVA' && (
              <div className="mb-3 text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={cargando}
              className="w-full py-2 bg-primary text-white rounded hover:bg-blue-800 disabled:opacity-50"
            >
              {cargando ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>

          {/* ─── Separador + Prueba gratis ─── */}
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-300"></div>
            <span className="text-xs text-slate-500">¿No tienes cuenta?</span>
            <div className="flex-1 h-px bg-slate-300"></div>
          </div>

          <button
            type="button"
            onClick={() => setMostrarRegistro(true)}
            className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 font-semibold shadow-md transition flex items-center justify-center gap-2"
          >
            ✨ Prueba 7 días gratis
          </button>
          <p className="text-[11px] text-center text-slate-500 mt-2">
            ✅ 2 dispositivos · ✅ 3 piscinas · ✅ Sin tarjeta
          </p>

          <p className="mt-4 text-center text-xs text-gray-500 lg:hidden">
            💡 Prueba el demo abriéndolo en pantalla grande
          </p>
        </div>
      </div>

      {/* Modal de registro */}
      {mostrarRegistro && (
        <RegistroDemoModal onClose={() => setMostrarRegistro(false)} />
      )}

      {/* MARCA: Logo + Lluchunga (arriba izquierda) */}
      <div className="absolute top-6 left-6 z-20 flex items-center gap-3 select-none pointer-events-none">
        <img
          src="/logo.png"
          alt="Lluchunga Technology"
          className="w-14 h-14 rounded-full bg-white/90 p-1 shadow-lg"
          style={{ objectFit: 'contain' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div className="flex flex-col leading-tight">
          <span
            className="text-3xl font-bold tracking-wide"
            style={{
              color: '#FFFFFF',
              textShadow: '0 2px 8px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.8)',
            }}
          >
            Lluchunga
          </span>
          <span
            className="text-[11px] font-medium tracking-widest uppercase"
            style={{
              color: 'rgba(255,255,255,0.85)',
              textShadow: '0 1px 3px rgba(0,0,0,0.7)',
            }}
          >
            Soporte Tecnológico
          </span>
        </div>
      </div>

      {/* FIRMA: abajo izquierda */}
      <div className="absolute bottom-6 left-6 z-20 select-none pointer-events-none">
        <span
          className="text-xs font-medium tracking-[0.25em] uppercase"
          style={{
            color: 'rgba(255,255,255,0.85)',
            textShadow: '0 1px 4px rgba(0,0,0,0.8), 0 0 12px rgba(0,0,0,0.5)',
          }}
        >
          © 2026 Lluchunga Technology
        </span>
      </div>
    </div>
  );
}